import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Clapperboard, LayoutGrid, List, Loader2, Search, Star } from 'lucide-react'

import { AssemblyRow } from '@/components/jobs/AssemblyRow'
import { JobCard } from '@/components/jobs/JobCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/use-toast'
import { apiFetch } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { useJobs } from '@/hooks/useJobs'
import type { AspectRatio, GenerationJob, JobStatus, OutputType } from '@/types'

// ---------------------------------------------------------------------------
// Ad stage badge helper
// ---------------------------------------------------------------------------

export function deriveAdStage(status: string): {
  label: string
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
  className?: string
} {
  switch (status) {
    case 'completed':
      return { label: 'Complete', variant: 'default', className: 'bg-green-500/10 text-green-600 border-green-500/30' }
    case 'failed':
      return { label: 'Failed', variant: 'destructive' }
    case 'queued':
    case 'processing':
      return { label: 'Generating', variant: 'secondary', className: 'animate-pulse' }
    case 'cancelled':
      return { label: 'Cancelled', variant: 'outline' }
    default:
      return { label: status, variant: 'outline' }
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Array<{ label: string; value: 'all' | JobStatus }> = [
  { label: 'All Status', value: 'all' },
  { label: 'Queued', value: 'queued' },
  { label: 'Processing', value: 'processing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
  { label: 'Cancelled', value: 'cancelled' },
]

const AR_OPTIONS: Array<{ label: string; value: 'all' | AspectRatio }> = [
  { label: 'All Ratios', value: 'all' },
  { label: '9:16', value: '9:16' },
  { label: '1:1', value: '1:1' },
  { label: '3:4', value: '3:4' },
]

const TYPE_OPTIONS: Array<{ label: string; value: 'all' | OutputType }> = [
  { label: 'All Types', value: 'all' },
  { label: 'Video', value: 'video' },
  { label: 'Image', value: 'image' },
]

const ACTIVE_STATUSES = new Set<JobStatus>(['queued', 'processing'])

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <div className="rounded-full bg-muted p-4">
        <Clapperboard className="h-8 w-8 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {filtered ? 'No jobs match your filters' : 'No jobs yet'}
        </p>
        <p className="text-xs text-muted-foreground">
          {filtered
            ? 'Try adjusting your search or filter criteria.'
            : 'Head to Generate to kick off your first video generation.'}
        </p>
      </div>
      {!filtered && (
        <Button asChild size="sm">
          <Link to="/generate">Go to Generate</Link>
        </Button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Skeleton grid
// ---------------------------------------------------------------------------

function SkeletonGrid() {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-36 w-full rounded-lg" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// List view row
// ---------------------------------------------------------------------------

interface ListRowProps {
  job: GenerationJob
  onStar: (jobId: number, currentlyStarred: boolean) => Promise<void>
}

function ListRow({ job, onStar }: ListRowProps) {
  const stage = deriveAdStage(job.status)
  const [starred, setStarred] = React.useState(job.is_starred)
  const [starPending, setStarPending] = React.useState(false)

  React.useEffect(() => {
    setStarred(job.is_starred)
  }, [job.is_starred])

  const handleStar = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (starPending) return
    const prev = starred
    setStarred(!prev)
    setStarPending(true)
    try {
      await onStar(job.id, prev)
    } catch {
      setStarred(prev)
    } finally {
      setStarPending(false)
    }
  }

  // Derive a short model name (last segment after '/')
  const modelShortName = job.model_id.split('/').pop() ?? job.model_id

  return (
    <Link
      to={`/created-assets/${job.id}`}
      className="flex items-center gap-3 px-4 py-3 border-b border-border hover:bg-muted/50 transition-colors"
    >
      <span className="text-xs text-muted-foreground w-10 shrink-0">#{job.id}</span>
      <span className="flex-1 text-sm truncate min-w-0">{job.prompt_text}</span>
      <Badge
        variant={stage.variant}
        className={`shrink-0 text-[10px] ${stage.className ?? ''}`}
      >
        {stage.label}
      </Badge>
      <span className="text-xs text-muted-foreground w-20 shrink-0 truncate hidden sm:block">
        {modelShortName}
      </span>
      <span className="text-xs text-muted-foreground w-10 shrink-0 hidden md:block">
        {job.aspect_ratio}
      </span>
      <span className="text-xs text-muted-foreground w-10 shrink-0 hidden md:block">
        {job.duration_seconds}s
      </span>
      <span className="text-xs text-muted-foreground w-16 shrink-0 hidden lg:block">
        {formatDate(job.created_at)}
      </span>
      <button
        type="button"
        onClick={handleStar}
        disabled={starPending}
        aria-label={starred ? 'Unstar job' : 'Star job'}
        className={`shrink-0 p-0.5 rounded transition-colors ${starred ? 'text-yellow-400' : 'text-muted-foreground hover:text-yellow-400'}`}
      >
        <Star className="h-3.5 w-3.5" fill={starred ? 'currentColor' : 'none'} />
      </button>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </Link>
  )
}

// ---------------------------------------------------------------------------
// List view header
// ---------------------------------------------------------------------------

function ListHeader() {
  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-border">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-10 shrink-0">#</span>
      <span className="flex-1 text-[10px] text-muted-foreground uppercase tracking-wide">Prompt</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide shrink-0 w-20">Status</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-20 shrink-0 hidden sm:block">Model</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-10 shrink-0 hidden md:block">AR</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-10 shrink-0 hidden md:block">Dur</span>
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide w-16 shrink-0 hidden lg:block">Date</span>
      <Star className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="w-4 shrink-0" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function Jobs() {
  const { toast } = useToast()
  const navigate = useNavigate()
  // 200/page so a user's full assembly-grouped history fits on page 1
  // (each assembly is 1 bucket but contains 4-6 underlying jobs — old
  // 50/page default meant only ~5-10 assemblies were visible).
  const {
    jobs,
    loading,
    error,
    refresh,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
  } = useJobs({ pageSize: 200 })

  const [searchParams, setSearchParams] = useSearchParams()
  const starredParam = searchParams.get('starred') === 'true'

  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<'all' | JobStatus>('all')
  const [arFilter, setArFilter] = React.useState<'all' | AspectRatio>('all')
  const [typeFilter, setTypeFilter] = React.useState<'all' | OutputType>('all')

  const [viewMode, setViewMode] = React.useState<'list' | 'grid'>(
    () => (localStorage.getItem('aioverlords_jobs_view') as 'list' | 'grid') || 'list',
  )

  React.useEffect(() => {
    localStorage.setItem('aioverlords_jobs_view', viewMode)
  }, [viewMode])

  const toggleStarredFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (starredParam) {
        next.delete('starred')
      } else {
        next.set('starred', 'true')
      }
      return next
    })
  }

  // ---- Bucketing (singles vs assemblies) ----------------------------------

  type Bucket =
    | { kind: 'single'; key: string; job: GenerationJob; createdAt: string; status: JobStatus }
    | {
        kind: 'assembly'
        key: string
        assemblyId: number
        scenes: GenerationJob[]
        stitched: GenerationJob | null
        createdAt: string
        status: JobStatus
        headlinePromptText: string
        aspectRatio: AspectRatio
        outputType: OutputType
        isStarred: boolean
      }

  function deriveAssemblyStatus(scenes: GenerationJob[], stitched: GenerationJob | null): JobStatus {
    if (stitched) return stitched.status
    if (scenes.some((s) => s.status === 'failed')) return 'failed'
    if (scenes.some((s) => s.status === 'processing')) return 'processing'
    if (scenes.some((s) => s.status === 'queued')) return 'queued'
    if (scenes.length > 0 && scenes.every((s) => s.status === 'completed')) return 'completed'
    if (scenes.length > 0 && scenes.every((s) => s.status === 'cancelled')) return 'cancelled'
    return 'queued'
  }

  const buckets = React.useMemo<Bucket[]>(() => {
    const assemblyMap = new Map<number, { scenes: GenerationJob[]; stitched: GenerationJob | null }>()
    const singles: GenerationJob[] = []

    for (const job of jobs) {
      if (job.assembly_id != null) {
        const entry = assemblyMap.get(job.assembly_id) ?? { scenes: [], stitched: null }
        if (job.output_kind === 'stitched') {
          entry.stitched = job
        } else {
          entry.scenes.push(job)
        }
        assemblyMap.set(job.assembly_id, entry)
      } else {
        singles.push(job)
      }
    }

    const result: Bucket[] = singles.map((job) => ({
      kind: 'single',
      key: `single-${job.id}`,
      job,
      createdAt: job.created_at,
      status: job.status,
    }))

    for (const [assemblyId, { scenes, stitched }] of assemblyMap) {
      scenes.sort((a, b) => (a.scene_number ?? 0) - (b.scene_number ?? 0))
      const headline = stitched ?? scenes[0] ?? null
      if (!headline) continue
      result.push({
        kind: 'assembly',
        key: `assembly-${assemblyId}`,
        assemblyId,
        scenes,
        stitched,
        createdAt: stitched?.created_at ?? scenes[0]?.created_at ?? headline.created_at,
        status: deriveAssemblyStatus(scenes, stitched),
        headlinePromptText: headline.prompt_text,
        aspectRatio: headline.aspect_ratio,
        outputType: headline.output_type,
        isStarred: (stitched ?? scenes[0])?.is_starred ?? false,
      })
    }

    result.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    return result
  }, [jobs])

  // ---- Filtering ----------------------------------------------------------

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    return buckets.filter((bucket) => {
      // Search — match against the bucket's headline prompt text (or the single's prompt).
      if (q !== '') {
        const promptText =
          bucket.kind === 'single' ? bucket.job.prompt_text : bucket.headlinePromptText
        if (!promptText.toLowerCase().includes(q)) return false
      }
      if (statusFilter !== 'all' && bucket.status !== statusFilter) return false

      if (bucket.kind === 'single') {
        if (arFilter !== 'all' && bucket.job.aspect_ratio !== arFilter) return false
        if (typeFilter !== 'all' && bucket.job.output_type !== typeFilter) return false
        if (starredParam && !bucket.job.is_starred) return false
      } else {
        if (arFilter !== 'all' && bucket.aspectRatio !== arFilter) return false
        if (typeFilter !== 'all' && bucket.outputType !== typeFilter) return false
        if (starredParam && !bucket.isStarred) return false
      }
      return true
    })
  }, [buckets, search, statusFilter, arFilter, typeFilter, starredParam])

  const totalCost = React.useMemo(
    () =>
      filtered.reduce((sum, bucket) => {
        if (bucket.kind === 'single') return sum + (bucket.job.cost_cents || 0)
        const sceneSum = bucket.scenes.reduce((s, j) => s + (j.cost_cents || 0), 0)
        return sum + sceneSum + (bucket.stitched?.cost_cents || 0)
      }, 0),
    [filtered],
  )

  const showActiveSection =
    statusFilter === 'all' ||
    statusFilter === 'queued' ||
    statusFilter === 'processing'

  const activeBuckets = showActiveSection
    ? filtered.filter((b) => ACTIVE_STATUSES.has(b.status))
    : []
  const restBuckets = filtered.filter((b) => !ACTIVE_STATUSES.has(b.status))
  const mainBuckets = showActiveSection ? restBuckets : filtered

  const isFiltered = search.trim() !== '' || statusFilter !== 'all' || arFilter !== 'all' || typeFilter !== 'all' || starredParam

  // ---- Handlers -----------------------------------------------------------

  const handleStar = async (jobId: number, currentlyStarred: boolean): Promise<void> => {
    const endpoint = currentlyStarred ? 'unstar' : 'star'
    const res = await apiFetch(`/api/v1/jobs/${jobId}/${endpoint}`, { method: 'POST' })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      toast({
        title: 'Error',
        description: data.detail || `Failed to ${endpoint} job`,
        variant: 'destructive',
      })
      throw new Error(data.detail || 'star toggle failed')
    }
    toast({
      title: currentlyStarred ? 'Unstarred' : 'Starred',
      description: currentlyStarred ? `Job #${jobId} removed from starred.` : `Job #${jobId} added to starred.`,
    })
    refresh()
  }

  const handleCancel = async (jobId: number) => {
    try {
      const res = await apiFetch(`/api/v1/jobs/${jobId}/cancel`, { method: 'POST' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Cancel failed')
      }
      toast({ title: 'Job cancelled', description: `Job #${jobId} has been cancelled.` })
      refresh()
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' })
    }
  }

  const handleDelete = async (jobId: number) => {
    try {
      const res = await apiFetch(`/api/v1/jobs/${jobId}`, { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Delete failed')
      }
      toast({ title: 'Job deleted', description: `Job #${jobId} removed.` })
      refresh()
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' })
    }
  }

  // ---- Render helpers -----------------------------------------------------

  const openJobDetail = (job: GenerationJob) => navigate(`/created-assets/${job.id}`)

  const renderBucketGrid = (bucketList: Bucket[]) => {
    const singles = bucketList.filter((b) => b.kind === 'single') as Extract<Bucket, { kind: 'single' }>[]
    return (
      <div className="space-y-4">
        {bucketList.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden">
            {bucketList.map((b) =>
              b.kind === 'assembly' ? (
                <AssemblyRow
                  key={b.key}
                  assemblyId={b.assemblyId}
                  scenes={b.scenes}
                  stitched={b.stitched}
                  onStar={handleStar}
                />
              ) : null,
            )}
          </div>
        )}
        {singles.length > 0 && (
          <div className="grid gap-3 md:grid-cols-2">
            {singles.map((b) => (
              <JobCard
                key={b.key}
                job={b.job}
                onSelect={openJobDetail}
                onCancel={handleCancel}
                onDelete={handleDelete}
                onStar={handleStar}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderBucketList = (bucketList: Bucket[]) => {
    if (bucketList.length === 0) return null
    return (
      <div className="rounded-lg border border-border overflow-hidden">
        <ListHeader />
        {bucketList.map((b) =>
          b.kind === 'assembly' ? (
            <AssemblyRow
              key={b.key}
              assemblyId={b.assemblyId}
              scenes={b.scenes}
              stitched={b.stitched}
              onStar={handleStar}
            />
          ) : (
            <ListRow key={b.key} job={b.job} onStar={handleStar} />
          ),
        )}
      </div>
    )
  }

  // ---- Render -------------------------------------------------------------

  return (
    <div className="space-y-8 max-w-5xl animate-fade-in">
      {/* Header */}
      <header className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Created Assets</h1>
        <p className="text-sm text-muted-foreground">Every video and image generation you've run.</p>
      </header>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prompts..."
            className="pl-9"
            aria-label="Search jobs by prompt text"
          />
        </div>

        {/* Starred chip */}
        <Badge
          variant={starredParam ? 'default' : 'outline'}
          className="cursor-pointer select-none flex items-center gap-1 px-3 py-1.5 text-xs"
          onClick={toggleStarredFilter}
          aria-pressed={starredParam}
          role="button"
        >
          <Star className="h-3 w-3" fill={starredParam ? 'currentColor' : 'none'} />
          Starred
        </Badge>

        {/* View toggle */}
        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${viewMode === 'list' ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
            onClick={() => setViewMode('list')}
            aria-label="List view"
            aria-pressed={viewMode === 'list'}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-foreground text-background' : 'text-muted-foreground'}`}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
            aria-pressed={viewMode === 'grid'}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>

        {/* Status filter */}
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as 'all' | JobStatus)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Aspect ratio filter */}
        <Select
          value={arFilter}
          onValueChange={(value) => setArFilter(value as 'all' | AspectRatio)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AR_OPTIONS.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Output type filter */}
        <Select
          value={typeFilter}
          onValueChange={(value) => setTypeFilter(value as 'all' | OutputType)}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_OPTIONS.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary row */}
      {!loading && (filtered.length > 0 || total > 0) && (
        <p className="text-xs text-muted-foreground -mt-4">
          Showing {filtered.length} grouped {filtered.length === 1 ? 'item' : 'items'}
          {total > 0 && (
            <>
              {' '}from {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{' '}
              {total} underlying jobs
            </>
          )}
          {totalCost > 0 && <> · ${(totalCost / 100).toFixed(2)} total cost</>}
        </p>
      )}

      {/* API error banner */}
      {error && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState filtered={isFiltered} />
      ) : (
        <div className="space-y-8">
          {/* Active jobs section */}
          {showActiveSection && activeBuckets.length > 0 && (
            <section className="space-y-3" aria-labelledby="active-jobs-heading">
              <h2
                id="active-jobs-heading"
                className="text-sm font-semibold uppercase tracking-widest text-muted-foreground flex items-center gap-2"
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                In Progress ({activeBuckets.length})
              </h2>
              {viewMode === 'grid' ? renderBucketGrid(activeBuckets) : renderBucketList(activeBuckets)}
            </section>
          )}

          {/* All jobs section */}
          {mainBuckets.length > 0 && (
            <section className="space-y-3" aria-labelledby="all-jobs-heading">
              <h2
                id="all-jobs-heading"
                className="text-sm font-semibold uppercase tracking-widest text-muted-foreground"
              >
                All Jobs ({mainBuckets.length})
              </h2>
              {viewMode === 'grid' ? renderBucketGrid(mainBuckets) : renderBucketList(mainBuckets)}
            </section>
          )}

          {!showActiveSection && filtered.length === 0 && (
            <EmptyState filtered={isFiltered} />
          )}
        </div>
      )}

      {/* Pagination — visible whenever there's more than one page of
          underlying jobs, even if client-side filters narrow the current
          page to zero buckets. */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages || loading}
              aria-label="Next page"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
